import http from "@/services/http";
import { featureFlags } from "@/config/env";
import { getSourceId } from "@/utils/constants";

// 声誉关键词数据缓存（函数外部，确保缓存持久化）
const reputationKeywordsCache = new Map<string, ReputationKeywordsResponse>();

export interface ReputationKeyword {
  keywordId?: string;
  name: string;
  value: number;
  taskIds: string;
}

export interface ReputationKeywordsResponse {
  positive?: ReputationKeyword[];
  negative?: ReputationKeyword[];
}

export interface ReputationKeywordsParams {
  brandId: string;
  categoryId: string;
  days?: number;
  startDay?: string; // 自定义开始日期，格式：2025-12-30
  endDay?: string; // 自定义结束日期，格式：2025-12-30
  metricType: string;
  modelId?: string;
  qId?: string; // 问题ID
  sourceId?: number; // 数据源ID：2=国内，3=国外
}

/**
 * 生成Mock声誉关键词数据（包含缓存机制）
 */
const generateMockReputationKeywords = (
  params: ReputationKeywordsParams
): ReputationKeywordsResponse => {
  // 生成缓存键（添加版本标识符，避免使用旧的缓存数据）
  const cacheKey = `reputation-v2-${params.brandId}-${params.categoryId}-${params.days || 1}`;

  // 先检查缓存
  if (reputationKeywordsCache.has(cacheKey)) {
    console.log("📦 使用缓存的声誉关键词数据");
    return reputationKeywordsCache.get(cacheKey)!;
  }
  // 根据天数调整数据量，但最多显示5个关键词
  const daysFactor = params.days || 1;
  const dataSize = Math.min(
    5,
    Math.max(3, Math.min(10, Math.floor(daysFactor / 3) + 3))
  );

  // 生成随机值的函数 - 确保权重差异明显
  const generateRandomValue = (baseValue: number, days: number): number => {
    // 基于天数的随机因子
    const dayMultiplier = Math.max(1, Math.min(5, Math.floor(days / 7)));
    // 随机波动范围：±50%，确保权重有明显差异
    const randomFactor = 0.5 + Math.random() * 1.0;
    return Math.round(baseValue * dayMultiplier * randomFactor);
  };

  // 正面关键词库（带随机值）- 大幅增加基础权重值
  const positiveKeywords = [
    {
      keywordId: "kw-positive-1",
      name: "性价比高",
      value: generateRandomValue(30, daysFactor),
      taskIds:
        "257e497d-77ed-11f0-be9b-8a1d1fb6f320,257e497d-77ed-11f0-be9b-8a1d1fb6f321",
    },
    {
      keywordId: "kw-positive-2",
      name: "质量好",
      value: generateRandomValue(20, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f322",
    },
    {
      keywordId: "kw-positive-3",
      name: "服务周到",
      value: generateRandomValue(40, daysFactor),
      taskIds:
        "257e497d-77ed-11f0-be9b-8a1d1fb6f323,257e497d-77ed-11f0-be9b-8a1d1fb6f324",
    },
    {
      keywordId: "kw-positive-4",
      name: "设计美观",
      value: generateRandomValue(20, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f325",
    },
    {
      keywordId: "kw-positive-5",
      name: "性能稳定",
      value: generateRandomValue(10, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f326",
    },
    {
      keywordId: "kw-positive-6",
      name: "续航优秀",
      value: generateRandomValue(30, daysFactor),
      taskIds:
        "257e497d-77ed-11f0-be9b-8a1d1fb6f327,257e497d-77ed-11f0-be9b-8a1d1fb6f328",
    },
    {
      keywordId: "kw-positive-7",
      name: "充电快速",
      value: generateRandomValue(20, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f329",
    },
  ];

  // 负面关键词库（带随机值）- 大幅增加基础权重值
  const negativeKeywords = [
    {
      keywordId: "kw-negative-1",
      name: "售后不统一",
      value: generateRandomValue(10, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f330",
    },
    {
      keywordId: "kw-negative-2",
      name: "价格偏高",
      value: generateRandomValue(30, daysFactor),
      taskIds:
        "257e497d-77ed-11f0-be9b-8a1d1fb6f331,257e497d-77ed-11f0-be9b-8a1d1fb6f332",
    },
    {
      keywordId: "kw-negative-3",
      name: "续航不足",
      value: generateRandomValue(10, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f333",
    },
    {
      keywordId: "kw-negative-4",
      name: "充电慢",
      value: generateRandomValue(10, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f334",
    },
    {
      keywordId: "kw-negative-5",
      name: "内饰简陋",
      value: generateRandomValue(10, daysFactor),
      taskIds: "257e497d-77ed-11f0-be9b-8a1d1fb6f335",
    },
    {
      keywordId: "kw-negative-6",
      name: "噪音大",
      value: generateRandomValue(20, daysFactor),
      taskIds:
        "257e497d-77ed-11f0-be9b-8a1d1fb6f336,257e497d-77ed-11f0-be9b-8a1d1fb6f337",
    },
  ];

  const result: ReputationKeywordsResponse = {};

  // 根据metricType参数返回对应数据
  const metricTypes = params.metricType.split(",");

  if (metricTypes.includes("positive")) {
    result.positive = positiveKeywords
      .slice(0, dataSize)
      .sort((a, b) => b.value - a.value);
  }

  if (metricTypes.includes("negative")) {
    result.negative = negativeKeywords
      .slice(0, dataSize)
      .sort((a, b) => b.value - a.value);
  }

  // 缓存新生成的数据
  reputationKeywordsCache.set(cacheKey, result);
  return result;
};

const reputationApi = {
  async getReputationKeywords(
    params: ReputationKeywordsParams
  ): Promise<ReputationKeywordsResponse> {
    // 使用内置的 Mock 数据生成器
    if (featureFlags.enableMock) {
      return generateMockReputationKeywords(params);
    }

    // 自动添加sourceId，如果没有指定则使用默认值
    const sourceId = params.sourceId ?? getSourceId();

    // 构建请求参数
    const requestParams: Record<string, any> = {
      ...params,
      sourceId,
    };

    // 移除undefined的参数
    Object.keys(requestParams).forEach((key) => {
      if (requestParams[key] === undefined) {
        delete requestParams[key];
      }
    });

    // 真实API调用
    try {
      const response = await http.get("data/brand/reputation/keywords", {
        params: requestParams,
      });
      // 提取data字段，返回ReputationKeywordsResponse结构
      return response.data.data;
    } catch (error) {
      console.error("获取品牌声誉关键词失败:", error);
      throw error;
    }
  },
};

export default reputationApi;
